import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import {
  Save, ArrowLeft, Settings as SettingsIcon, Loader2, RotateCcw,
  Plus, X, GripVertical, Pencil, Check
} from 'lucide-react';
import MasterListsEditor from '../../components/MasterListsEditor';

const DEFAULT_LEAD_SLA = { New: 2, Qualified: 5, Qualification: 5, Proposal: 7, Negotiation: 10, Closed: 9999 };
const DEFAULT_OPP_SLA = {
  'Enquiry Received': 3, Qualification: 5, 'BOQ Prep': 7, Proposal: 7, Negotiation: 10, Awarded: 9999, Won: 9999, Lost: 9999,
};
const DEFAULT_OPP_STAGES = ['Enquiry Received', 'Qualification', 'BOQ Prep', 'Proposal', 'Negotiation', 'Awarded', 'Won', 'Lost'];

export default function ERPConfiguration() {
  const navigate = useNavigate();
  const [leadSla, setLeadSla] = useState({});
  const [oppSla, setOppSla] = useState({});
  const [oppStages, setOppStages] = useState([]);
  const [autoTask, setAutoTask] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('pipeline');

  const load = async () => {
    try {
      const cfg = await apiGet('/crm/sla/config');
      setLeadSla(cfg.lead_sla || DEFAULT_LEAD_SLA);
      setOppSla(cfg.opportunity_sla || DEFAULT_OPP_SLA);
      setOppStages(cfg.opportunity_stages || DEFAULT_OPP_STAGES);
      setAutoTask(!!cfg.auto_create_followup_task);
    } catch (err) {
      toast.error('Failed to load ERP config');
    }
  };
  useEffect(() => { load(); }, []);

  const updateSlaCell = (group, key, val) => {
    const v = val === '' ? '' : Math.max(0, parseInt(val) || 0);
    const setter = group === 'lead' ? setLeadSla : setOppSla;
    setter(prev => ({ ...prev, [key]: v }));
  };

  const save = async () => {
    if (oppStages.some((s) => !s || !s.trim())) {
      toast.error('Pipeline stages cannot be empty'); return;
    }
    const dupes = oppStages.filter((s, i) => oppStages.indexOf(s) !== i);
    if (dupes.length) { toast.error(`Duplicate stage: ${dupes[0]}`); return; }
    if (oppStages.length < 2) { toast.error('Need at least 2 pipeline stages'); return; }

    setSaving(true);
    try {
      // Coerce empty SLA values to 9999 (never stale).
      // Drop SLA entries whose stage no longer exists; ensure every stage has a row.
      const coerce = (map) => Object.fromEntries(Object.entries(map).map(([k, v]) => [k, v === '' ? 9999 : v]));
      const lead = coerce(leadSla);
      const oppNext = {};
      for (const s of oppStages) {
        oppNext[s] = oppSla[s] === '' || oppSla[s] === undefined ? 9999 : oppSla[s];
      }
      await apiPut('/crm/sla/config', {
        lead_sla: lead,
        opportunity_sla: oppNext,
        opportunity_stages: oppStages,
        auto_create_followup_task: autoTask,
      });
      toast.success('ERP configuration saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setLeadSla(DEFAULT_LEAD_SLA);
    setOppSla(DEFAULT_OPP_SLA);
    setOppStages(DEFAULT_OPP_STAGES);
    toast.info('Reset to defaults — click Save to persist');
  };

  return (
    <div className="p-6 space-y-6" data-testid="erp-config-page">
      <button
        onClick={() => navigate('/app/settings')}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-display font-bold text-slate-500 hover:text-[#1F7A6E]"
        data-testid="back-to-settings"
      >
        <ArrowLeft size={14} /> Back to Settings
      </button>

      <PageHeader
        title="ERP Configuration"
        subtitle="Single source of truth for platform-level settings — pipeline stages, SLAs, automation rules, and master dropdown lists used everywhere in the ERP."
        icon={SettingsIcon}
      />

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto" data-testid="erp-config-tabs">
        {[
          { id: 'pipeline', label: 'Pipeline Stages' },
          { id: 'sla', label: 'SLA Rules' },
          { id: 'masters', label: 'Master Lists' },
          { id: 'automation', label: 'Automation' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`erp-tab-${t.id}`}
            className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider rounded-t-sm transition-colors -mb-px border-b-2 whitespace-nowrap ${
              tab === t.id ? 'border-[#1F7A6E] text-[#1F7A6E] bg-white' : 'border-transparent text-slate-500 hover:text-[#1F7A6E]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && (
        <PipelineStageEditor
          stages={oppStages}
          slaMap={oppSla}
          onChangeStages={setOppStages}
          onChangeSla={(key, v) => updateSlaCell('opp', key, v)}
        />
      )}

      {tab === 'sla' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SLACard
            title="Lead Stage SLAs"
            subtitle="Days a lead may stay in a stage before turning stale."
            values={leadSla}
            onChange={(k, v) => updateSlaCell('lead', k, v)}
            testIdPrefix="lead-sla"
          />
          <SLACard
            title="Opportunity / Project Pipeline SLAs"
            subtitle="Days a deal may stay in a pipeline stage before being flagged."
            values={oppSla}
            onChange={(k, v) => updateSlaCell('opp', k, v)}
            testIdPrefix="opp-sla"
          />
        </div>
      )}

      {tab === 'masters' && <MasterListsEditor />}

      {tab === 'automation' && (
        <div className="bg-white border border-slate-200 rounded-sm p-5 max-w-xl">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoTask}
              onChange={e => setAutoTask(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#1F7A6E] cursor-pointer"
              data-testid="auto-task-checkbox"
            />
            <div>
              <div className="font-display font-semibold text-sm text-[#0F172A]">Auto-create follow-up task on stale records</div>
              <div className="text-xs text-slate-500 mt-0.5">When enabled, a task is auto-assigned to the owner whenever a record becomes stale per the SLA above.</div>
            </div>
          </label>

          <div className="mt-6 pt-6 border-t border-slate-100 text-[11px] text-slate-400 uppercase tracking-widest">
            More automation rules (email, WhatsApp, escalation) will arrive in the next iterations.
          </div>
        </div>
      )}

      {tab !== 'masters' && (
        <div className="flex items-center gap-3 pt-2 sticky bottom-0 bg-white/90 backdrop-blur py-3 border-t border-slate-100">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
            data-testid="save-erp-config"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
          <button
            onClick={resetDefaults}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-300 hover:border-[#1F7A6E] hover:text-[#1F7A6E] text-xs uppercase tracking-wider font-display font-bold rounded-sm"
            data-testid="reset-defaults"
          >
            <RotateCcw size={14} /> Reset Defaults
          </button>
        </div>
      )}
    </div>
  );
}

function PipelineStageEditor({ stages, slaMap, onChangeStages, onChangeSla }) {
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [newStage, setNewStage] = useState('');
  const [dragIdx, setDragIdx] = useState(null);

  const commitRename = (idx) => {
    const v = editVal.trim();
    if (!v) { toast.error('Stage name cannot be empty'); return; }
    if (stages.some((s, i) => i !== idx && s.toLowerCase() === v.toLowerCase())) {
      toast.error('Duplicate stage name'); return;
    }
    const oldName = stages[idx];
    const next = stages.slice();
    next[idx] = v;
    // Carry SLA value across rename
    if (oldName in slaMap && oldName !== v) {
      onChangeSla(v, slaMap[oldName] ?? 9999);
      onChangeSla(oldName, ''); // mark for removal — save() filters by current stage list
    }
    onChangeStages(next);
    setEditIdx(null);
    setEditVal('');
  };

  const removeStage = (idx) => {
    if (stages.length <= 2) { toast.error('Need at least 2 stages'); return; }
    if (!window.confirm(`Remove stage "${stages[idx]}"? Existing deals in that stage will need to be re-categorised.`)) return;
    const next = stages.filter((_, i) => i !== idx);
    onChangeStages(next);
  };

  const addStage = () => {
    const v = newStage.trim();
    if (!v) return;
    if (stages.some((s) => s.toLowerCase() === v.toLowerCase())) {
      toast.error('Stage already exists'); return;
    }
    onChangeStages([...stages, v]);
    onChangeSla(v, 7);
    setNewStage('');
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const next = stages.slice();
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    onChangeStages(next);
    setDragIdx(null);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="pipeline-editor">
      <div>
        <div className="font-display font-bold text-sm uppercase tracking-wider text-[#0F172A]">Project Pipeline Stages</div>
        <div className="text-xs text-slate-500 mt-1">Drag to reorder. Rename or remove stages. SLA in days controls the stale-deal flag.</div>
      </div>

      <div className="space-y-2">
        {stages.map((s, idx) => (
          <div
            key={`${s}-${idx}`}
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(idx)}
            className={`flex items-center gap-3 border rounded-sm px-3 py-2 hover:border-[#1F7A6E] transition-colors ${dragIdx === idx ? 'opacity-50' : ''} ${idx === 0 ? 'bg-cyan-50/40 border-cyan-200' : 'border-slate-200'}`}
            data-testid={`pipeline-stage-row-${idx}`}
          >
            <GripVertical size={14} className="text-slate-300 cursor-grab shrink-0" />
            <span className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-400 w-6">{idx + 1}</span>
            {editIdx === idx ? (
              <input
                type="text"
                value={editVal}
                onChange={(e) => setEditVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') commitRename(idx); if (e.key === 'Escape') { setEditIdx(null); setEditVal(''); } }}
                autoFocus
                className="flex-1 input"
                data-testid={`pipeline-stage-edit-${idx}`}
              />
            ) : (
              <span className="flex-1 font-display font-semibold text-sm text-[#0F172A]">{s}</span>
            )}
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                value={slaMap[s] === 9999 ? '' : (slaMap[s] ?? '')}
                placeholder="∞"
                onChange={(e) => onChangeSla(s, e.target.value)}
                className="w-20 px-2 py-1 text-right text-sm border border-slate-200 focus:border-[#1F7A6E] rounded-sm"
                data-testid={`pipeline-stage-sla-${idx}`}
              />
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">days</span>
            </div>
            {editIdx === idx ? (
              <button
                onClick={() => commitRename(idx)}
                className="text-emerald-600 hover:text-emerald-800"
                title="Save name"
                data-testid={`pipeline-stage-save-${idx}`}
              ><Check size={14} /></button>
            ) : (
              <button
                onClick={() => { setEditIdx(idx); setEditVal(s); }}
                className="text-slate-400 hover:text-[#1F7A6E]"
                title="Rename stage"
                data-testid={`pipeline-stage-rename-${idx}`}
              ><Pencil size={13} /></button>
            )}
            <button
              onClick={() => removeStage(idx)}
              className="text-slate-400 hover:text-red-600"
              title="Remove stage"
              data-testid={`pipeline-stage-remove-${idx}`}
            ><X size={14} /></button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
        <input
          type="text"
          value={newStage}
          onChange={(e) => setNewStage(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addStage(); }}
          placeholder="New stage name (e.g. Site Survey)"
          className="input flex-1"
          data-testid="pipeline-stage-new-name"
        />
        <button
          onClick={addStage}
          disabled={!newStage.trim()}
          className="inline-flex items-center gap-1 px-3 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
          data-testid="pipeline-stage-add"
        >
          <Plus size={13} /> Add Stage
        </button>
      </div>

      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800">
        ⚠ Removing or renaming a stage does not move existing deals automatically. Update each deal's stage before/after saving to avoid orphan records.
      </div>
    </div>
  );
}

function SLACard({ title, subtitle, values, onChange, testIdPrefix }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="mb-4">
        <div className="font-display font-bold text-sm uppercase tracking-wider text-[#0F172A]">{title}</div>
        <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
      </div>
      <div className="space-y-3">
        {Object.entries(values).map(([stage, days]) => (
          <div key={stage} className="flex items-center justify-between gap-4">
            <label className="text-sm font-semibold text-[#0F172A] min-w-0 flex-1 truncate">{stage}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                value={days === 9999 ? '' : days}
                placeholder="∞ (never)"
                onChange={e => onChange(stage, e.target.value)}
                className="w-24 px-2 py-1.5 text-right text-sm border border-slate-200 focus:border-[#1F7A6E] rounded-sm"
                data-testid={`${testIdPrefix}-${stage.replace(/\s+/g, '-').toLowerCase()}`}
              />
              <span className="text-xs text-slate-500 w-8">days</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
